// ✅ AdminAchievementVerification (ALTERED ONLY FOR THEME + endpoint invocation style)
// - Matches your “above theme” (orange header bar + white title, BG #F2F2F2, soft blue borders, zebra/hover surfaces)
// - Invokes backend like your CodeSubCampus page: inline headers in each axios call (no memo headers)
// - No business/API logic changed

import React, { useEffect, useState } from "react";
import axios from "axios";
import dayjs from "dayjs";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Chip,
  Alert,
  Divider,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { API_URL } from "./config";

const AdminAchievementVerification = () => {
  // ✅ Theme constants (same pattern as your “above theme”)
  const THEME_ORANGE = "#F07828";
  const THEME_ORANGE_2 = "#ed7f35ff";
  const BG = "#F2F2F2";
  const BORDER = "rgba(25,118,210,0.20)";
  const BORDER_HOVER = "rgba(25,118,210,0.45)";
  const BORDER_FOCUS = "rgba(240,120,40,0.80)";
  const ROW_ALT = "rgba(25,118,210,0.06)";
  const ROW_HOVER = "rgba(25,118,210,0.10)";

  const pageWrapSx = { p: 2, backgroundColor: BG, minHeight: "100vh" };

  const rootPaperSx = {
    mx: "auto",
    borderRadius: 2,
    overflow: "hidden",
    border: "1px solid rgba(25,118,210,0.15)",
  };

  const headerSx = {
    px: 2,
    py: 1.5,
    background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
    color: "white",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 2,
    flexWrap: "wrap",
  };

  const bodySx = { p: 2, backgroundColor: BG };

  const surfaceSx = {
    borderRadius: 2,
    backgroundColor: "white",
    border: `1px solid ${BORDER}`,
  };

  const inputSx = {
    "& .MuiOutlinedInput-notchedOutline": { borderColor: BORDER },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_HOVER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: BORDER_FOCUS },
  };

  const primaryBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    backgroundColor: THEME_ORANGE,
    "&:hover": { backgroundColor: THEME_ORANGE_2 },
  };

  const outlinedBtnSx = {
    borderRadius: 2,
    textTransform: "none",
    fontWeight: 900,
    borderColor: "rgba(25,118,210,0.35)",
    color: "rgba(25,118,210,0.95)",
    "&:hover": {
      borderColor: "rgba(25,118,210,0.55)",
      backgroundColor: "rgba(25,118,210,0.04)",
    },
  };

  const chipSx = {
    bgcolor: "rgba(240,120,40,0.10)",
    border: "1px solid rgba(240,120,40,0.25)",
    fontWeight: 800,
  };

  // ✅ Token (use the same pattern you used elsewhere; fallback to older key)
  const token = localStorage.getItem("auth") || localStorage.getItem("token");

  // ================== Original state/logic ==================
  const [status, setStatus] = useState("pending");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [error, setError] = useState("");

  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [action, setAction] = useState("verified");
  const [comment, setComment] = useState("");

  const load = async () => {
    setError("");
    try {
      const resp = await axios.get(`${API_URL}/api/career/admin/achievements`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { status, q: q.trim() || undefined },
      });
      setRows(resp.data || []);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to load achievements");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const submitVerify = async () => {
    if (!selected) return;
    setError("");
    try {
      await axios.post(
        `${API_URL}/api/career/admin/achievements/${selected.AchievementId}/verify`,
        { action, comment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOpen(false);
      setSelected(null);
      setComment("");
      await load();
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || "Failed to update verification");
    }
  };

  const statusPillSx = (s) => {
    if (s === "verified")
      return {
        bgcolor: "rgba(46,125,50,0.08)",
        border: "1px solid rgba(46,125,50,0.25)",
        color: "rgba(46,125,50,0.95)",
        fontWeight: 900,
      };
    if (s === "rejected")
      return {
        bgcolor: "rgba(211,47,47,0.07)",
        border: "1px solid rgba(211,47,47,0.25)",
        color: "rgba(211,47,47,0.95)",
        fontWeight: 900,
      };
    return chipSx; // pending/default
  };

  return (
    <Box sx={pageWrapSx}>
      <Paper elevation={5} sx={rootPaperSx}>
        {/* ✅ Orange header bar */}
        <Box sx={headerSx}>
          <Box>
            <Typography sx={{ fontSize: "1.1rem", fontWeight: 900, lineHeight: 1.2 }}>
              Achievement Verification
            </Typography>
            <Typography sx={{ opacity: 0.9, fontSize: "0.85rem" }}>
              Review student achievements and verify / reject with comments
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" sx={outlinedBtnSx} onClick={load}>
              Refresh
            </Button>
          </Box>
        </Box>

        <Box sx={bodySx}>
          {!!error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{
                borderRadius: 2,
                border: "1px solid rgba(244,67,54,0.25)",
                backgroundColor: "rgba(244,67,54,0.04)",
                mb: 2,
              }}
            >
              {error}
            </Alert>
          )}

          {/* ✅ Filters surface */}
          <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl sx={{ minWidth: 220 }} size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  label="Status"
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                  sx={inputSx}
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="verified">Verified</MenuItem>
                  <MenuItem value="rejected">Rejected</MenuItem>
                  <MenuItem value="all">All</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                size="small"
                label="Search (StudentID / Title / Event)"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                sx={inputSx}
              />

              <Button variant="contained" onClick={load} sx={primaryBtnSx}>
                Search
              </Button>
            </Stack>
          </Paper>

          <Divider sx={{ my: 2 }} />

          {/* ✅ List (cards) styled like your surfaces + zebra feel */}
          <Stack spacing={1.2}>
            {rows.length === 0 ? (
              <Alert severity="info" sx={{ borderRadius: 2 }}>
                No records.
              </Alert>
            ) : (
              rows.map((r, idx) => (
                <Paper
                  key={r.AchievementId}
                  elevation={0}
                  sx={{
                    ...surfaceSx,
                    p: 1.5,
                    backgroundColor: idx % 2 === 0 ? ROW_ALT : "#fff",
                    "&:hover": { backgroundColor: ROW_HOVER },
                  }}
                >
                  <Stack direction="row" justifyContent="space-between" gap={2} alignItems="flex-start">
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }} noWrap>
                        {r.Title}
                      </Typography>

                      <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.3 }}>
                        Student: <b>{r.StudentID || "—"}</b> • {r.AchievementType} • {r.OrgOrEvent || "—"} •{" "}
                        {r.Level || "—"} • {r.Outcome || "—"}
                      </Typography>

                      <Typography variant="caption" sx={{ color: "text.secondary" }}>
                        Updated: {dayjs(r.UpdatedAt).format("DD MMM YYYY, hh:mm A")} • Status:{" "}
                        <b>{r.VerifiedStatus}</b>
                      </Typography>

                      <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                        <Chip size="small" label={r.VerifiedStatus} sx={statusPillSx(r.VerifiedStatus)} />
                        {r.ProofUrl ? (
                          <Chip size="small" label="Has Proof URL" sx={{ bgcolor: "rgba(25,118,210,0.06)" }} />
                        ) : null}
                      </Stack>

                      {r.VerificationComment ? (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <b>Comment:</b> {r.VerificationComment}
                        </Typography>
                      ) : null}
                    </Box>

                    <Button
                      variant="outlined"
                      onClick={() => {
                        setSelected(r);
                        setAction(r.VerifiedStatus === "rejected" ? "rejected" : "verified");
                        setComment(r.VerificationComment || "");
                        setOpen(true);
                      }}
                      sx={outlinedBtnSx}
                    >
                      Verify / Reject
                    </Button>
                  </Stack>
                </Paper>
              ))
            )}
          </Stack>
        </Box>
      </Paper>

      {/* ✅ Dialog themed */}
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontWeight: 900,
            color: "white",
            background: `linear-gradient(90deg, ${THEME_ORANGE} 0%, ${THEME_ORANGE_2} 100%)`,
          }}
        >
          Verification Action
        </DialogTitle>

        <DialogContent dividers sx={{ backgroundColor: BG }}>
          <Paper elevation={0} sx={{ ...surfaceSx, p: 2 }}>
            {selected ? (
              <>
                <Typography sx={{ fontWeight: 900, color: "rgba(25,118,210,0.95)" }}>
                  {selected.Title}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Student: <b>{selected.StudentID || "—"}</b>
                </Typography>

                <FormControl fullWidth sx={{ mt: 2 }} size="small">
                  <InputLabel>Action</InputLabel>
                  <Select
                    label="Action"
                    value={action}
                    onChange={(e) => setAction(e.target.value)}
                    sx={inputSx}
                  >
                    <MenuItem value="verified">Verified</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                    <MenuItem value="pending">Pending</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  sx={{ mt: 2, ...inputSx }}
                  fullWidth
                  size="small"
                  label="Comment (optional)"
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  multiline
                  minRows={3}
                />
              </>
            ) : null}
          </Paper>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, backgroundColor: BG }}>
          <Button onClick={() => setOpen(false)} sx={outlinedBtnSx}>
            Cancel
          </Button>
          <Button variant="contained" onClick={submitVerify} sx={primaryBtnSx}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminAchievementVerification;
